import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import TableLoadingRow from '../shared/TableLoadingRow'
import TableEmptyState from '../shared/TableEmptyState'
import {
  STATUS_PENDING,
  STATUS_REVIEWED,
  formatGapPct,
  formatMoney,
  formatSubmittedDate,
  isImageProof,
  statusLabel,
  submitterDisplay,
} from './priceConcernUtils'

const COL_COUNT = 8

function statusSelectSx(reviewed) {
  const palette = reviewed
    ? {
        bg: '#E9F7EF',
        border: '#B7E4CB',
        color: '#157F4A',
        borderHover: '#9fd4b8',
        borderFocus: '#157F4A',
      }
    : {
        bg: '#FDF1E1',
        border: '#F0C98A',
        color: '#A9600C',
        borderHover: '#e5b870',
        borderFocus: '#A9600C',
      }

  return {
    minWidth: 148,
    '& .MuiOutlinedInput-root': {
      borderRadius: '999px',
      bgcolor: palette.bg,
      fontSize: 12,
      fontWeight: 700,
      letterSpacing: '0.02em',
      '& fieldset': {
        borderColor: palette.border,
        borderRadius: '999px',
      },
      '&:hover fieldset': {
        borderColor: palette.borderHover,
      },
      '&.Mui-focused fieldset': {
        borderColor: palette.borderFocus,
        borderWidth: 1,
      },
    },
    '& .MuiSelect-select': {
      py: 0.625,
      px: 1.75,
      pr: '28px !important',
      color: palette.color,
    },
    '& .MuiSelect-icon': {
      color: palette.color,
      right: 8,
    },
  }
}

export function PriceConcernsTable({
  rows,
  total,
  loading,
  page,
  rowsPerPage,
  statusUpdatingId,
  onPageChange,
  onRowsPerPageChange,
  onOpenDetail,
  onStatusChange,
  onOpenProof,
}) {
  return (
    <TableContainer
      component={Paper}
      sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider' }}
    >
      <Table size="small" sx={{ minWidth: 960 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: '#f8f9fb' }}>
            <TableCell>Product</TableCell>
            <TableCell>Shop</TableCell>
            <TableCell align="right">Shwapno Price</TableCell>
            <TableCell align="right">Reported Price</TableCell>
            <TableCell>Gap %</TableCell>
            <TableCell>Proof</TableCell>
            <TableCell>Submitted</TableCell>
            <TableCell>Status</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableLoadingRow colSpan={COL_COUNT} />
          ) : rows.length === 0 ? (
            <TableEmptyState
              colSpan={COL_COUNT}
              message="No price concerns match the current filters."
            />
          ) : (
            rows.map((row) => {
              const updating = statusUpdatingId === row.id
              const reviewed = row.status === STATUS_REVIEWED
              return (
                <TableRow key={row.id} hover>
                  <TableCell
                    sx={{ cursor: 'pointer', maxWidth: 260 }}
                    onClick={() => onOpenDetail(row)}
                  >
                    <Typography fontWeight={600} noWrap title={row.product_name}>
                      {row.product_name || '—'}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      #{row.article_code || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography noWrap title={row.shop_name}>
                      {row.shop_name || '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Shop ID {row.org_id}
                    </Typography>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                  >
                    {formatMoney(row.shwapno_price)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
                  >
                    {formatMoney(row.dealer_price)}
                  </TableCell>
                  <TableCell>
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: '#FDF1E1',
                        color: '#A9600C',
                        fontWeight: 800,
                        fontSize: 12,
                      }}
                    >
                      {formatGapPct(row.gap_pct)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {row.proof ? (
                      <Button
                        size="small"
                        startIcon={
                          isImageProof(row.proof) ? (
                            <ImageOutlinedIcon fontSize="small" />
                          ) : (
                            <InsertDriveFileOutlinedIcon fontSize="small" />
                          )
                        }
                        onClick={(e) => {
                          e.stopPropagation()
                          onOpenProof(row)
                        }}
                        sx={{ textTransform: 'none', color: 'info.main' }}
                      >
                        View
                      </Button>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        None
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatSubmittedDate(row.submitted_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {submitterDisplay(row.submitted_by)}
                    </Typography>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <TextField
                        select
                        size="small"
                        value={reviewed ? STATUS_REVIEWED : STATUS_PENDING}
                        disabled={updating}
                        onChange={(e) => {
                          const next = e.target.value
                          if (next === STATUS_REVIEWED || next === STATUS_PENDING) {
                            onStatusChange(row.id, next)
                          }
                        }}
                        sx={statusSelectSx(reviewed)}
                      >
                        <MenuItem value={STATUS_PENDING}>
                          {statusLabel(STATUS_PENDING)}
                        </MenuItem>
                        <MenuItem value={STATUS_REVIEWED}>
                          {statusLabel(STATUS_REVIEWED)}
                        </MenuItem>
                      </TextField>
                      {updating && <CircularProgress size={16} />}
                    </Box>
                  </TableCell>
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={(_, p) => onPageChange(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) =>
          onRowsPerPageChange(parseInt(e.target.value, 10))
        }
        rowsPerPageOptions={[10, 20, 50]}
      />
    </TableContainer>
  )
}
